import { createBrowserRouter } from 'react-router-dom'
import { AppLayout } from './AppLayout.tsx'
import { NotFoundPage } from './NotFoundPage.tsx'
import { RedirectIfSignedIn, RequireTeacher } from './RequireTeacher.tsx'
import { RouteError } from './RouteError.tsx'

/**
 * Two worlds on one origin: everything under the layout needs a teacher session, and
 * `/l/:slug` is public and deliberately carries none of the teacher chrome. `/` itself is not
 * here: it and `/ro/` `/ru/` `/fr/` are static landing pages (apps/web/landing), so the hub is
 * `/app`.
 */
export const router = createBrowserRouter([
	{
		// Outermost: the public pages and anything the shell's own boundary does not catch.
		errorElement: <RouteError bare />,
		children: [
			{
				element: (
					<RequireTeacher>
						<AppLayout />
					</RequireTeacher>
				),
				children: [
					// A pathless route with its own boundary: a failed page renders inside the layout's
					// Outlet, so the header stays, which an error on the layout route itself would not.
					{
						errorElement: <RouteError />,
						children: [
							{
								path: 'app',
								lazy: () =>
									import('../features/hub/HubPage.tsx').then((m) => ({ Component: m.HubPage })),
							},
							{
								path: 'folders/:folderId',
								lazy: () =>
									import('../features/folders/FolderPage.tsx').then((m) => ({
										Component: m.FolderPage,
									})),
							},
							{
								path: 'tools/exercises',
								lazy: async () => {
									const [{ NewSourcePage }, { ToolIntro }] = await Promise.all([
										import('../features/source/NewSourcePage.tsx'),
										import('../features/onboarding/ToolIntro.tsx'),
									])
									return { element: <NewSourcePage intro={<ToolIntro id="exercises" />} /> }
								},
							},
							{
								path: 'tools/exercises/source/:artifactId',
								lazy: () =>
									import('../features/editor/ArtifactPage.tsx').then((m) => ({
										Component: m.ArtifactPage,
									})),
							},
							{
								path: 'tools/exercises/:activityId',
								lazy: () =>
									import('../features/editor/ActivityEditorPage.tsx').then((m) => ({
										Component: m.ActivityEditorPage,
									})),
							},
							{
								path: 'tools/wheel',
								lazy: () =>
									import('../features/wheel/WheelPage.tsx').then((m) => ({
										Component: m.WheelPage,
									})),
							},
							{
								path: 'tools/words',
								lazy: () =>
									import('../features/words/WordsHomePage.tsx').then((m) => ({
										Component: m.WordsHomePage,
									})),
							},
							{
								path: 'tools/words/new/:artifactId',
								lazy: () =>
									import('../features/words/MakeWordListPage.tsx').then((m) => ({
										Component: m.MakeWordListPage,
									})),
							},
							{
								path: 'tools/words/:id',
								lazy: () =>
									import('../features/words/WordListPage.tsx').then((m) => ({
										Component: m.WordListPage,
									})),
							},
							{
								path: 'tools/words/:id/results',
								lazy: () =>
									import('../features/words/WordResultsPage.tsx').then((m) => ({
										Component: m.WordResultsPage,
									})),
							},
							{
								path: 'tools/words/:id/puzzles',
								lazy: () =>
									import('../features/words/PuzzlePage.tsx').then((m) => ({
										Component: m.PuzzlePage,
									})),
							},
							{
								path: 'tools/exercises/:activityId/print',
								lazy: () =>
									import('../features/print/WorksheetPage.tsx').then((m) => ({
										Component: m.WorksheetPage,
									})),
							},
							{
								path: 'tools/levels',
								lazy: () =>
									import('../features/levels/entries.tsx').then((m) => ({
										Component: m.LevelsPickPage,
									})),
							},
							{
								path: 'tools/levels/:artifactId',
								lazy: () =>
									import('../features/levels/LevelsPage.tsx').then((m) => ({
										Component: m.LevelsPage,
									})),
							},
							{
								path: 'tools/plans',
								lazy: () =>
									import('../features/plans/entries.tsx').then((m) => ({
										Component: m.PlansPickPage,
									})),
							},
							{
								path: 'tools/plans/source/:artifactId',
								lazy: () =>
									import('../features/plans/PlanCreatePage.tsx').then((m) => ({
										Component: m.PlanCreatePage,
									})),
							},
							{
								path: 'tools/plans/:planId',
								lazy: () =>
									import('../features/plans/PlanPage.tsx').then((m) => ({ Component: m.PlanPage })),
							},
							{
								path: 'results',
								lazy: () =>
									import('../features/results/ResultsHubPage.tsx').then((m) => ({
										Component: m.ResultsHubPage,
									})),
							},
							{
								path: 'activities/:activityId/results',
								lazy: () =>
									import('../features/results/ResultsPage.tsx').then((m) => ({
										Component: m.ResultsPage,
									})),
							},
						],
					},
				],
			},
			{
				path: '/login',
				lazy: () =>
					import('../features/auth/AuthPages.tsx').then((m) => ({
						element: (
							<RedirectIfSignedIn>
								<m.LoginPage />
							</RedirectIfSignedIn>
						),
					})),
			},
			{
				path: '/register',
				lazy: () =>
					import('../features/auth/AuthPages.tsx').then((m) => ({
						element: (
							<RedirectIfSignedIn>
								<m.RegisterPage />
							</RedirectIfSignedIn>
						),
					})),
			},
			{
				// Her own activity as a student sees it: signed in, but none of the teacher chrome.
				path: '/tools/exercises/:activityId/preview',
				lazy: () =>
					import('../features/preview/PreviewPage.tsx').then((m) => ({
						element: (
							<RequireTeacher>
								<m.PreviewPage />
							</RequireTeacher>
						),
					})),
			},
			{
				path: '/l/:slug',
				lazy: () =>
					import('../features/learner/LearnerPage.tsx').then((m) => ({ Component: m.LearnerPage })),
			},
			{
				path: '/w/:slug',
				lazy: () =>
					import('../features/words/StudentWordsPage.tsx').then((m) => ({
						Component: m.StudentWordsPage,
					})),
			},
			// Dev only: every primitive and state (app/LabPage.tsx). Lazy, so it never reaches the build.
			...(import.meta.env.DEV
				? [
						{
							path: '/lab',
							lazy: async () => ({ Component: (await import('./LabPage.tsx')).LabPage }),
						},
					]
				: []),
			{ path: '*', element: <NotFoundPage /> },
		],
	},
])
